export function getPercent(used: number, limit: number | null): number {
  if (limit === null || limit === 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

export function getBarColor(percent: number): string {
  if (percent >= 100) return 'bg-status-error';
  if (percent >= 80) return 'bg-status-warning';
  return 'bg-brand-tertiary';
}

export function getStatusColor(percent: number): string {
  if (percent >= 100) return 'text-status-error-text';
  if (percent >= 80) return 'text-status-warning-text';
  return 'text-content-secondary';
}

export interface UsageBreakdown {
  mcp: number;
  web: number;
  /** True only when there is an MCP portion worth surfacing a breakdown for. */
  hasBreakdown: boolean;
}

/**
 * Split a combined usage total into its MCP and web portions (#10111 unified
 * quota). `web = total - mcp` (clamped ≥ 0; mcp itself clamped into [0, total]
 * to survive any transient over-count), and `hasBreakdown` is true only when
 * mcp > 0 — so a caller can hide the web-vs-MCP tooltip when every unit came
 * from the web (the common case) and show it only when MCP actually contributed.
 */
export function getUsageBreakdown(
  total: number,
  mcp: number | undefined
): UsageBreakdown {
  const mcpUsed = Math.max(0, Math.min(mcp ?? 0, total));
  return {
    mcp: mcpUsed,
    web: Math.max(0, total - mcpUsed),
    hasBreakdown: mcpUsed > 0,
  };
}

export function formatDate(dateStr: string | null, locale: string): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export function formatCountdown(resetsAt: string | null): string {
  if (!resetsAt) return '';
  const diffMs = new Date(resetsAt).getTime() - Date.now();
  if (diffMs <= 0) return '';
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}
